import { useCallback, useContext, useState } from 'react';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { ExternalMedia, InternalMedia, Post, Source } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { MediaSource } from '@sodamnfoolish/sjc-backend-types/src/api/feed/media-source';
import { providers } from 'ethers';

import { SolPriceProvider } from '../../Components/SolPriceProvider';
import InteractionBanner from '../../Components/Banner';
import { TimerProvder } from '../../Components/TimeProvider';
import * as Virtual from '../../Components/VirtualGrid/VirtualGrid';
import { TwitterUserInfoProvider } from '../../Components/twitter/TwitterUserInfo';
import { playSound } from '../../Hooks/posts/sound';

import { EveryoneColumn, SourceColumn, dispatchHighlightPost, Post as PostCard, DateTime } from './FeedColumns';
import { LiveDataBonkTokensProvider } from './context/LiveDataBonkTokensContext';
import { LiveDataPumpTokensProvider } from './context/LiveDataPumpTokensContext';
import { SoundStoreContext } from './context/SoundStoreContext';
import { TweetTypesContext } from './context/TweetTypesContext';
import { FeedProvider } from './context/FeedContext';
import { BonkLinkedProvider, PumpFunLinkedProvider } from './context/LinkedContext';
import { PumpFunBubblesProvider } from './context/PumpFunBubblesContext';
import { PumpFunColumn } from './PumpFunColumn';
import { BonkBubblesProvider } from './context/BonkBubblesContext';
import { LetsBonkColumn } from './LetsBonkColumn';
import { AppMode } from '../../types';
import { Connection, Keypair, LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import bs58 from 'bs58';
import { BN } from '@coral-xyz/anchor';
import { UserSettings } from '../Settings';
import { buyLetsbonkToken } from '../../letsbonk/letsbonk';
import Modal from '../../Components/Modal';
import { TokenCreate } from '../TokenCreate/TokenCreate';
import { solididtyReleases } from '../../utils/types';
import { ImageSource, TokenSource } from '../TokenCreate/types';
import { SourceDetails } from '../../utils/source-constants';
import { capitalizeFirstLetter, getFirstWord, removeDoubleStar } from '../../utils/string';
import { base64ToFile } from '../../utils/base64-to-file';
import { blobToBase64, blobToFile } from '../../utils/blob';
import { useBackend } from '../../Hooks/UseBackend';
import { urlToFile } from '../../utils/url-to-file';
import { TweetActions } from '../TokenCreate/TweetActions';
import { ExternalLinkIcon } from '../../Components/icons/ExternalLink';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import { getTwitterIdFromUrl } from '../../utils/ge-tweet-id-from-url';
import { buyPumpfunToken, decodeBondingCurve, getBondingCurvePubkey } from '../../pumpfun/pumpfun';
import { toast } from 'react-toastify';
import { buyPumpswapToken, getPumpswapPoolId } from '../../pumpswap/pumpswap';
import { getPdaLaunchpadPoolId, LAUNCHPAD_PROGRAM, LaunchpadPool } from '@raydium-io/raydium-sdk-v2';
import { NATIVE_MINT } from '@solana/spl-token';
import { calculateRequiredMicroLamportsFromTxCost } from '../../utils/solana/calculate-required-microLamports-from-tx-cost';

export function Feed({
    sources,
    isGeneralPostsVisible,
    isPumpFunsPostsVisible,
    isBonkPostsVisible,
    sourceOrder,
    solidityReleases,
    connection,
    ethProvider,
    settings,
    onAddPurchasedSolToken,
    onAddDeployedSolToken,
}: {
    sources: Source[];
    isGeneralPostsVisible: boolean;
    isPumpFunsPostsVisible: boolean;
    isBonkPostsVisible: boolean;
    sourceOrder: string[];

    solidityReleases: solididtyReleases;
    connection: Connection;
    ethProvider: providers.JsonRpcProvider;
    settings: {
        mode: AppMode;
    } & UserSettings;
    onAddPurchasedSolToken: (address: string, name: string, symbol: string, image: string) => void;
    onAddDeployedSolToken: (address: string, name: string, symbol: string, image: string) => void;
}) {
    const { getMedia, getChatGptMetadataFromPostId } = useBackend();

    const buyPumpToken = useCallback(
        (token: Api.Pump.DTO.PumpFunToken, solAmount: BN) => {
            const buyer = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(settings.solanaSniperPrivateKey)));
            connection.getAccountInfo(getBondingCurvePubkey(new PublicKey(token.mint)), 'processed').then((info) => {
                if (!info) return toast.error('Bonding curve not found');

                const decoded = decodeBondingCurve(info);
                console.log('decoded', decoded);
                if (decoded.complete) {
                    getPumpswapPoolId(connection, new PublicKey(token.mint)).then((poolId) => {
                        if (!poolId) throw new Error('pool not found');
                        console.log('poolId', poolId.toBase58());
                        buyPumpswapToken(
                            connection,
                            poolId,
                            new PublicKey(token.mint),
                            solAmount,
                            buyer,
                            settings.zeroSlotBuyTip * LAMPORTS_PER_SOL,
                            150_000,
                            Number(calculateRequiredMicroLamportsFromTxCost(settings.solBuyTxCost, 150_000)),
                        );
                    });
                } else {
                    buyPumpfunToken(
                        connection,
                        new PublicKey(token.mint),
                        buyer,
                        solAmount,
                        settings.zeroSlotBuyTip * LAMPORTS_PER_SOL,
                        150_000,
                        Number(calculateRequiredMicroLamportsFromTxCost(settings.solBuyTxCost, 150_000)),
                    );
                }
                onAddPurchasedSolToken(token.mint, token.name, token.symbol, token.image ?? '');
            });
        },
        [connection, settings.solanaSniperPrivateKey, settings.zeroSlotBuyTip],
    );

    const buyBonkToken = useCallback(
        (token: Api.Bonk.DTO.LetsBonkToken, solAmount: BN) => {
            const buyer = Keypair.fromSecretKey(Uint8Array.from(bs58.decode(settings.solanaSniperPrivateKey)));
            connection.getAccountInfo(getPdaLaunchpadPoolId(LAUNCHPAD_PROGRAM, new PublicKey(token.mint), NATIVE_MINT).publicKey, 'processed').then((info) => {
                if (!info) return toast.error('Launchpad pool not found');

                const decoded = LaunchpadPool.decode(info.data);
                if (decoded.status === 0) {
                    buyLetsbonkToken(connection, new PublicKey(token.mint), buyer, decoded.creator, solAmount, settings.zeroSlotBuyTip * LAMPORTS_PER_SOL);
                } else {
                }
                onAddPurchasedSolToken(token.mint, token.name, token.symbol, token.image ?? '');
            });
        },
        [connection, settings.solanaSniperPrivateKey, settings.zeroSlotBuyTip],
    );

    const orderedSources = sourceOrder.map((username) => sources.find((source) => source.username === username)).filter(Boolean) as Source[];

    const soundStore = useContext(SoundStoreContext);
    const tweetTypesStore = useContext(TweetTypesContext);

    const [createTokenRequest, setCreateTokenRequest] = useState<
        | {
              originType: 'post';
              origin: Post & { details: SourceDetails };
              tokenSource: Partial<TokenSource>;
          }
        | {
              originType: 'token';
              origin: Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken;
              tokenSource: Partial<TokenSource>;
          }
        | null
    >(null);

    const createTokenFromPost = useCallback(
        async (post: Post, details: SourceDetails) => {
            const partialTokenSource = await convertPostToTokenSource(post, details, getMedia);

            setCreateTokenRequest({
                origin: {
                    ...post,
                    details,
                },
                originType: 'post',
                tokenSource: partialTokenSource,
            });
        },
        [getMedia],
    );

    const createToken = useCallback(async (token: Api.Pump.DTO.PumpFunToken | Api.Bonk.DTO.LetsBonkToken) => {
        const imageSrc = token.image ?? '';

        let imageFile: File | null = null;

        try {
            imageFile = await urlToFile(imageSrc, 'metadataImage.jpg', 'image/jpeg');
        } catch {}

        setCreateTokenRequest({
            origin: token,
            originType: 'token',
            tokenSource: {
                name: token.name,
                symbol: token.symbol,
                description: token.description ?? '',
                imageSrc,
                imageFile,
                telegram: token.telegram ?? '',
                twitter: token.twitter ?? '',
                website: token.website ?? '',
            },
        });
    }, []);

    return (
        <>
            <InteractionBanner />
            <TimerProvder>
                <SolPriceProvider>
                    <PumpFunBubblesProvider enabled>
                        <BonkBubblesProvider enabled>
                            <LiveDataPumpTokensProvider>
                                <LiveDataBonkTokensProvider>
                                    <FeedProvider
                                        sources={sources}
                                        onNewPost={(post) => {
                                            const soundState = soundStore.get();
                                            const tweetTypes = tweetTypesStore.get();

                                            if (soundState[post.username]) {
                                                playSound(post, tweetTypes);
                                            }

                                            setTimeout(() => {
                                                dispatchHighlightPost(post);
                                            }, 300);
                                        }}
                                    >
                                        <PumpFunLinkedProvider sources={sources}>
                                            <BonkLinkedProvider sources={sources}>
                                                <TwitterUserInfoProvider>
                                                    <Virtual.Grid key='grid' buffer={2} headerHeight={38} className='min-h-screen text-ash-grey'>
                                                        <EveryoneColumn id='Everyone' width={281} onCreate={createTokenFromPost} />
                                                        <LetsBonkColumn
                                                            id='Lets Bonk'
                                                            width={358}
                                                            mode={settings.mode}
                                                            onCreate={createToken}
                                                            onBuy={buyBonkToken}
                                                        />
                                                        <PumpFunColumn
                                                            id='PumpFun'
                                                            width={358}
                                                            mode={settings.mode}
                                                            onCreate={createToken}
                                                            onBuy={buyPumpToken}
                                                        />
                                                        {orderedSources.map((source) => (
                                                            <SourceColumn
                                                                key={source.username}
                                                                id={source.username}
                                                                width={281}
                                                                source={source}
                                                                onCreate={createTokenFromPost}
                                                            />
                                                        ))}
                                                    </Virtual.Grid>
                                                </TwitterUserInfoProvider>
                                            </BonkLinkedProvider>
                                        </PumpFunLinkedProvider>
                                    </FeedProvider>
                                </LiveDataBonkTokensProvider>
                            </LiveDataPumpTokensProvider>
                        </BonkBubblesProvider>
                    </PumpFunBubblesProvider>
                </SolPriceProvider>
            </TimerProvder>
            <Modal
                open={Boolean(createTokenRequest)}
                onClose={() => {
                    setCreateTokenRequest(null);
                }}
            >
                <TokenCreate
                    defaultName={createTokenRequest?.tokenSource.name}
                    defaultSymbol={createTokenRequest?.tokenSource.symbol}
                    defaultDescription={createTokenRequest?.tokenSource.description}
                    defaultImageSrc={createTokenRequest?.tokenSource.imageSrc}
                    defaultImageFile={createTokenRequest?.tokenSource.imageFile}
                    defaultTelegram={createTokenRequest?.tokenSource.telegram}
                    defaultTwitter={createTokenRequest?.tokenSource.twitter}
                    defaultWebsite={createTokenRequest?.tokenSource.website}
                    profilePic={createTokenRequest?.originType === 'post' ? createTokenRequest.origin.details.image : ''}
                    defaultEthLiquidity={createTokenRequest?.originType === 'post' ? settings.ethLiquidityArrow : settings.ethLiquidityCopy}
                    defaultDevBuySolana={createTokenRequest?.originType === 'post' ? settings.solInitialBuyArrow : settings.solInitialBuyCopy}
                    defaultSnipeBuySolana={createTokenRequest?.originType === 'post' ? settings.solSnipeArrow : settings.solSnipeCopy}
                    autoSellDelay={createTokenRequest?.originType === 'post' ? settings.autoSellDelayArrow : settings.autoSellDelayCreation}
                    autoSellProfit={createTokenRequest?.originType === 'post' ? settings.autoSellProfitArrow : settings.autoSellProfitCreation}
                    ethPrivateKey={settings.ethPrivateKey}
                    solPrivateKey={settings.solanaPrivateKey}
                    solSniperPrivateKey={settings.solanaSniperPrivateKey}
                    ethSniperPrivateKeys={settings.ethSniperPrivateKeys}
                    ethSnipeValues={settings.ethSnipeValues}
                    solidityReleases={solidityReleases}
                    zeroSlotBuyTip={settings.zeroSlotBuyTip}
                    buyTxCost={settings.solBuyTxCost}
                    solConnection={connection}
                    ethProvider={ethProvider}
                    telegramAPIID={settings.telegramAPIID}
                    telegramAPIHash={settings.telegramAPIHash}
                    telegramPhoneNumber={settings.telegramPhoneNumber}
                    getAIMetadata={createTokenRequest?.originType === 'post' ? () => getChatGptMetadataFromPostId(createTokenRequest.origin.id) : null}
                    leftColumnSlot={
                        createTokenRequest?.originType === 'post' && (
                            <>
                                <PostCard
                                    key={createTokenRequest.origin.id}
                                    post={createTokenRequest.origin}
                                    createButtonPosition='body'
                                    renderHeader={(post, stroke, image, formattedElapsedTime) => (
                                        <div className='flex items-center'>
                                            <img className={'flex w-6 h-6 mr-[10px] border-[2px] rounded-[50%] ' + stroke} src={image} alt='avatar' />
                                            <span className='flex-1 font-bold'>{post.username}</span>
                                            <span className='mr-[14px]'>{formattedElapsedTime}</span>
                                            <button
                                                className='size-[24px] opacity-49 bg-[image:var(--twitter-logo-svg)] bg-contain cursor-pointer'
                                                onClick={() => {}}
                                            />
                                        </div>
                                    )}
                                    renderFooter={(post) => (
                                        <div className='flex items-center'>
                                            <button className='size-[24px] mr-[10px] cursor-pointer'>
                                                <ExternalLinkIcon />
                                            </button>
                                            <DateTime className='text-[10px]' date={post.date} />
                                        </div>
                                    )}
                                />
                                <div data-theme='dark' style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                                    <div
                                        className='display-tweet-form'
                                        data-theme='dark'
                                        style={{
                                            position: 'relative',
                                            width: '100%',

                                            overflow: 'hidden',
                                            zIndex: 2,
                                        }}
                                    >
                                        <TwitterTweetEmbed tweetId={getTwitterIdFromUrl(createTokenRequest.origin.url)} options={{ theme: 'dark' }} />
                                    </div>
                                </div>
                            </>
                        )
                    }
                    imageManipulationComponents={
                        createTokenRequest?.originType === 'post'
                            ? [
                                  function PostTweetActions({ onImageChange }: ImageSource) {
                                      return <TweetActions url={createTokenRequest.origin.url} onImageChange={onImageChange} />;
                                  },
                              ]
                            : []
                    }
                    onAddDeployedSolToken={onAddDeployedSolToken}
                />
            </Modal>
        </>
    );
}

async function convertPostToTokenSource(post: Post, details: SourceDetails, getMedia: (id: string) => Promise<Blob>): Promise<Partial<TokenSource>> {
    let name = '';
    let symbol = '';

    let description = '';

    if (post && post.text) {
        name = capitalizeFirstLetter(getFirstWord(post.text, false));
        symbol = getFirstWord(post.text, true);
        description = removeDoubleStar(post.text);
    }

    let imageSrc = `data:image/jpeg;base64,${details.image}`;
    let imageFile = details.image ? base64ToFile(imageSrc, 'sourceImage.jpg') : null;

    if (post && post.media && post.media.length > 0) {
        const mediaItem = post.media[0];
        try {
            if (mediaItem.source === MediaSource.Internal) {
                const blob = await getMedia((mediaItem as InternalMedia).id);
                const base64Image = await blobToBase64(blob);
                imageSrc = base64Image;
                imageFile = blobToFile(blob, 'postImage.jpg');
            } else if (mediaItem.source === MediaSource.External) {
                const response = await fetch((mediaItem as ExternalMedia).url);
                const blob = await response.blob();
                const base64Image = await blobToBase64(blob);
                imageSrc = base64Image;
                imageFile = blobToFile(blob, 'postImage.jpg');
            }
        } catch (error) {
            console.error('Error fetching media:', error);
        }
    }

    return {
        name,
        symbol,
        description,
        imageSrc,
        imageFile,
        twitter: post.url,
    };
}
